import React from "react";
import { Box, Typography } from "@mui/material";
import { useIsGreek } from "../../context/LanguageContext";

export const MPPT_ARRAY_KEYS = [
  "mppt_strings_per_tracker", "mppt_max_input_current_a", "mppt_max_short_circuit_current_a",
];

export function hasExplicitMppt(chars = {}) {
  return MPPT_ARRAY_KEYS.some((key) => chars[key] != null);
}

// One interpretation for both detail dialogs. Only an absent array may fall
// back to a scalar; a missing entry must never borrow another tracker's limit.
export function buildMpptRows(chars = {}, greek = false) {
  if (!hasExplicitMppt(chars)) return [];
  const count = Math.min(64, Math.max(
    Number(chars["Num MPPTs"] ?? chars.numMPPT) || 0,
    ...MPPT_ARRAY_KEYS.map((key) => Array.isArray(chars[key]) ? chars[key].length : 0),
  ));
  const fields = [
    [MPPT_ARRAY_KEYS[0], ["Num Inputs per MPPT", "numInptMPPT"], greek ? "Είσοδοι σειρών" : "String inputs", ""],
    [MPPT_ARRAY_KEYS[1], ["Idcmax_per_mppt"], greek ? "Μέγιστο ρεύμα λειτουργίας" : "Maximum operating current", "A"],
    [MPPT_ARRAY_KEYS[2], ["Iscmax_per_mppt"], greek ? "Μέγιστο ρεύμα βραχυκύκλωσης" : "Maximum short-circuit current", "A"],
  ];
  return Array.from({ length: count }, (_, index) => fields.map(([key, scalarKeys, label, unit]) => {
    const raw = chars[key] != null ? chars[key]?.[index]
      : scalarKeys.map((scalar) => chars[scalar]).find((value) => value != null);
    const value = raw !== "" && raw != null ? Number(raw) : NaN;
    return {
      label: `MPPT ${index + 1} — ${label}`,
      value: Number.isFinite(value) && value > 0
        ? `${value.toLocaleString("en-GB", { maximumFractionDigits: 4 })}${unit ? ` ${unit}` : ""}`
        : "—",
    };
  })).flat();
}

export default function InverterMpptSummary({ chars = {} }) {
  const greek = useIsGreek();
  const rows = buildMpptRows(chars, greek);
  const trackers = chars["Num MPPTs"] ?? chars.numMPPT;
  const inputs = chars["Num Inputs per MPPT"] ?? chars.numInptMPPT;
  if (!rows.length && trackers == null) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {rows.length ? (greek ? "Όρια ανά MPPT" : "Limits per MPPT") : "MPPTs × inputs"}
      </Typography>
      {rows.length ? rows.map((row) => (
        <Typography key={row.label} variant="caption" sx={{ display: "block", overflowWrap: "anywhere" }}>
          {row.label}: {row.value}
        </Typography>
      )) : <Typography variant="body2">{trackers} × {inputs ?? "—"}</Typography>}
    </Box>
  );
}
